import { useEffect, useState } from "react";
import { api } from "../lib/api";

const abs = (u) => (u || "").startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${u}` : u;

/** Rotating admin-created ads — shown to every user on the dashboard. */
export default function AdsRotator({ fitCreative = false }) {
  const [ads, setAds] = useState([]);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    api.get("/ads/active").then((r) => {
      const activeAds = Array.isArray(r.data) ? r.data : [];
      // The public sign-in ad needs a real campaign title; ignore placeholder
      // records such as "." so they cannot appear as broken blank promotions.
      setAds(fitCreative ? activeAds.filter(ad => typeof ad.title === "string" && /[A-Za-z0-9]{2,}/.test(ad.title.trim())) : activeAds);
    }).catch(() => {});
  }, [fitCreative]);
  useEffect(() => {
    if (ads.length < 2 || (fitCreative && (paused || window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches))) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % ads.length), 6000);
    return () => clearInterval(t);
  }, [ads.length, fitCreative, paused]);

  if (!ads.length) return null;
  const ad = ads[idx];

  const click = () => {
    api.post(`/admin/ads/${ad.id}/click`).catch(() => {});
    if (ad.target_url) {
      const url = new URL(ad.target_url, window.location.origin);
      if (["http:", "https:"].includes(url.protocol)) window.open(url.href, "_blank", "noopener,noreferrer");
    }
  };

  if (fitCreative) {
    const cleanTitle = ad.title.trim();
    const safeTarget = (() => {
      try {
        const target = typeof ad.target_url === "string" ? ad.target_url.trim() : "";
        if (!target) return "";
        const url = new URL(target, window.location.origin);
        return ["http:", "https:"].includes(url.protocol) ? url.href : "";
      } catch { return ""; }
    })();
    const Card = safeTarget ? "a" : "article";
    const cardLinkProps = safeTarget ? { href: safeTarget, target: "_blank", rel: "noopener noreferrer", "aria-label": `Open sponsored promotion: ${cleanTitle}`, onClick: () => api.post(`/admin/ads/${ad.id}/click`).catch(() => {}) } : {};
    return (
      <Card {...cardLinkProps} data-testid="ads-rotator" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocusCapture={() => setPaused(true)} onBlurCapture={e => { if (!e.currentTarget.contains(e.relatedTarget)) setPaused(false); }}
           className={`icaard-sponsored-card${safeTarget ? " is-clickable" : ""}`}>
        <div className="icaard-sponsored-art">
          {ad.media_type === "video"
            ? <video src={abs(ad.media_url)} autoPlay muted loop playsInline className="icaard-sponsored-media" />
            : <img src={abs(ad.media_url)} alt={cleanTitle} className="icaard-sponsored-media" />}
          <span className="icaard-sponsored-label">Sponsored</span>
          {ads.length > 1 && <span className="icaard-sponsored-pagination">{idx + 1} / {ads.length}</span>}
        </div>
        <div className="icaard-sponsored-copy">
          <div className="icaard-sponsored-copy-text">
            <strong>{cleanTitle}</strong>
            {ad.description && <span>{ad.description}</span>}
          </div>
          {safeTarget && <span className="icaard-sponsored-cta">View promotion <span aria-hidden="true">↗</span></span>}
        </div>
      </Card>
    );
  }

  return (
    <div onClick={click} role="button" tabIndex={0} aria-label={`Advertisement: ${ad.title}`} onKeyDown={e=>{if(e.key==="Enter"||e.key===" "){e.preventDefault();click();}}} data-testid="ads-rotator"
         className="relative overflow-hidden rounded-2xl border border-[#26263A] cursor-pointer group">
      {ad.media_type === "video"
        ? <video src={abs(ad.media_url)} autoPlay muted loop playsInline className="w-full max-h-56 object-cover" />
        : <img src={abs(ad.media_url)} alt={ad.title} className="w-full max-h-56 object-cover group-hover:scale-[1.02] transition-transform duration-500" />}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-4 py-2.5">
        <span className="text-[8px] uppercase tracking-[0.3em] font-black text-[#F59E0B]">Sponsored</span>
        <div className="text-sm font-bold text-white truncate">{ad.title}</div>
        {ad.description && <div className="text-[10px] text-white/70 truncate">{ad.description}</div>}
      </div>
      {ads.length > 1 && (
        <div className="absolute top-2 right-2 flex gap-1">
          {ads.map((_, i) => <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === idx ? "bg-white" : "bg-white/40"}`} />)}
        </div>
      )}
    </div>
  );
}
